import React, { useCallback, useEffect, useReducer, useState } from "react"
import { obterResumoDashboard } from "../../../services/resumoFornecedor"
import useAppStore from "../../../stores/AppStore"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import { ResumoFornecedorInicial } from "../models/resumoFornecedorModel"
import { ResumoFornecedorActionsTypes } from "./resumoFornecedorActions"
import { ResumoFornecedorContext } from "./ResumoFornecedorContext"
import { resumoFornecedorReducer } from "./resumoFornecedorReducer"

function pad(n: number): string {
    return String(n).padStart(2, "0")
}

function dataStr(d: Date): string {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// Última semana (7 dias até hoje) — usada em toda a tela inicial (KPIs e gráficos).
function janelaSemana(): { inicio: string; fim: string } {
    const fim = new Date()
    const inicio = new Date(fim)
    inicio.setDate(inicio.getDate() - 6)
    return { inicio: dataStr(inicio), fim: dataStr(fim) }
}

export function ResumoFornecedorContextProvider({
    children,
    modoInicial = false,
}: {
    children: React.ReactNode
    modoInicial?: boolean
}) {
    const [dados, dispatch] = useReducer(resumoFornecedorReducer, ResumoFornecedorInicial)
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const [carregando, setCarregando] = useState(false)
    const [carregouInicial, setCarregouInicial] = useState(false)
    const setLoading = useLoadingStore(s => s.setLoading)

    // CLIENTEFIEL troca de loja com frequência: na tela inicial os dados não são
    // carregados automaticamente — o usuário clica em "Ver dados" para buscar.
    const perfil = useAppStore(s => s.configuracoes)?.user?.perfil
    const carregamentoManual = modoInicial && perfil === "CLIENTEFIEL"

    const buscar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setCarregando(true)
            setLoading(true)
            const response = await obterResumoDashboard(
                autorizacao,
                dados.dataInicio,
                dados.dataFim
            )
            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar resumo")
            } else {
                setErroCarregamento(null)
                dispatch({ type: ResumoFornecedorActionsTypes.CARREGAR, payload: response })
            }
        } catch {
            setErroCarregamento("Erro ao carregar resumo")
        } finally {
            setCarregando(false)
            setLoading(false)
        }
    }, [dados.dataInicio, dados.dataFim, setLoading])

    // Tela inicial: KPIs, gráficos e séries usam sempre a última semana (últimos 7 dias).
    const buscarInicial = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setCarregouInicial(true)
        try {
            setCarregando(true)
            setLoading(true)
            const semana = janelaSemana()
            const response = await obterResumoDashboard(autorizacao, semana.inicio, semana.fim)
            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar resumo")
            } else {
                setErroCarregamento(null)
                dispatch({ type: ResumoFornecedorActionsTypes.CARREGAR, payload: response })
            }
        } catch {
            setErroCarregamento("Erro ao carregar resumo")
        } finally {
            setCarregando(false)
            setLoading(false)
        }
    }, [setLoading])

    useEffect(() => {
        if (modoInicial) {
            // CLIENTEFIEL: aguarda o clique em "Ver dados"; demais perfis carregam na hora.
            if (!carregamentoManual) buscarInicial()
        } else {
            buscar()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <ResumoFornecedorContext.Provider
            value={{ dados, dispatch, erroCarregamento, carregando, buscar, buscarInicial, carregouInicial }}
        >
            <Loading />
            {children}
        </ResumoFornecedorContext.Provider>
    )
}
